import { useEffect, useRef } from 'react';
import { EventForName } from '@fullstory/shared/eventmaps';

export interface EventHandlerFor<Name extends string> {
  (e: EventForName<Name>): void;
}
export interface EventTargetFor<Name extends string> {
  addEventListener(name: Name, cb: EventHandlerFor<Name>, options?: AddEventListenerOptions): void;
  removeEventListener(name: Name, cb: EventHandlerFor<Name>): void;
}

export interface UseEventListenerProps<Name extends string> {
  /**
   * Name of the event
   */
  name: Name;
  handler: EventHandlerFor<Name>;
  /**
   * Event target; defaults to `window`.
   */
  target?: EventTargetFor<Name>;
  options?: AddEventListenerOptions;

  /**
   * Listen for events only while `attached` is true (default).
   */
  attached?: boolean;
}

/**
 * Hook for adding an event handler to any event target.
 */
export function useEventListener<Name extends string>({
  name,
  target = window,
  handler,
  attached = true,
  options,
}: UseEventListenerProps<Name>): void {
  // Create a ref that stores handler
  const savedHandler = useRef<(e: EventForName<Name>) => void>();

  useEffect(() => {
    // Update ref.current value if handler changes.
    // This allows our effect below to always get latest handler ...
    // ... without us needing to pass it in effect deps array ...
    // ... and potentially cause effect to re-run every render.
    savedHandler.current = handler;
  }, [handler]);

  // We don't want to use the potentially-ephemeral `options` object as
  // a memo key..
  const { capture, passive } = options ?? {};
  useEffect(() => {
    // Create event listener that calls handler function stored in ref
    const eventListener = (event: EventForName<Name>): void => {
      savedHandler.current?.(event);
    };

    // Add event listener
    if (attached) target.addEventListener(name, eventListener, { capture, passive });

    // Remove event listener on cleanup
    return () => {
      if (attached) target.removeEventListener(name, eventListener);
    };
  }, [name, target, attached, capture, passive]);
}
